import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ChatInterface from './components/ChatInterface';
import HelpContentLibrary from './components/HelpContentLibrary';
import QuickHelpTopics from './components/QuickHelpTopics';
import { aiHelpService } from '../../lib/aiHelpService';

const AIHelpAssistant = () => {
  const { user, userProfile } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeView, setActiveView] = useState('chat'); // chat, library, topics
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [helpContent, setHelpContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(`session-${Date.now()}`);

  useEffect(() => {
    if (user && isOpen) {
      initializeConversation();
      loadHelpContent();
    }
  }, [user, isOpen]);

  useEffect(() => {
    // Track screen changes for contextual help
    if (isOpen && conversation) {
      updateConversationContext();
    }
  }, [location?.pathname]);

  const initializeConversation = async () => {
    const screenName = getScreenName(location?.pathname);
    const contextData = {
      userRole: userProfile?.role,
      gymId: userProfile?.gym_id,
      screenName
    };

    const { data } = await aiHelpService?.getOrCreateConversation(
      user?.id,
      sessionId?.current,
      screenName,
      contextData
    );

    if (data) {
      setConversation(data);
      loadMessages(data?.id);
    }
  };

  const loadMessages = async (conversationId) => {
    const { data } = await aiHelpService?.getConversationMessages(conversationId);
    if (data) {
      setMessages(data);
    }
  };

  const loadHelpContent = async () => {
    const screenName = getScreenName(location?.pathname);
    const { data } = await aiHelpService?.getHelpContent(screenName, userProfile?.role);
    if (data) {
      setHelpContent(data);
    }
  };

  const updateConversationContext = async () => {
    // Update conversation context when user navigates to different screen
    const screenName = getScreenName(location?.pathname);
    await aiHelpService?.trackInteraction(
      user?.id,
      'content_viewed',
      screenName
    );
  };

  const getScreenName = (pathname) => {
    const screenMap = {
      '/staff-dashboard': 'staff-dashboard',
      '/member-dashboard': 'member-dashboard',
      '/trainer-dashboard': 'trainer-dashboard',
      '/pos-inventory-management': 'pos-inventory-management',
      '/location-based-pos-inventory': 'location-based-pos-inventory',
      '/member-management': 'member-management',
      '/class-booking': 'class-booking',
      '/analytics-dashboard': 'analytics-dashboard',
      '/financial-reconciliation-dashboard': 'financial-reconciliation-dashboard'
    };
    return screenMap?.[pathname] || 'general';
  };

  const handleSendMessage = async (userMessage) => {
    if (!conversation || !userMessage?.trim()) return;

    setLoading(true);

    // Save user message
    await aiHelpService?.saveMessage(conversation?.id, 'user', userMessage);
    setMessages(prev => [...prev, { role: 'user', content: userMessage, created_at: new Date()?.toISOString() }]);

    // Track interaction
    await aiHelpService?.trackInteraction(
      user?.id,
      'question_asked',
      getScreenName(location?.pathname),
      null,
      { question: userMessage }
    );

    try {
      // Generate AI response
      const contextData = {
        userRole: userProfile?.role,
        screenName: getScreenName(location?.pathname),
        gymId: userProfile?.gym_id
      };

      const aiResponse = await aiHelpService?.generateAIResponse(
        userMessage,
        messages,
        contextData
      );

      // Save AI response
      await aiHelpService?.saveMessage(
        conversation?.id,
        'assistant',
        aiResponse?.answer,
        {
          suggestedActions: aiResponse?.suggestedActions,
          relatedTopics: aiResponse?.relatedTopics
        }
      );

      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: aiResponse?.answer,
          metadata: {
            suggestedActions: aiResponse?.suggestedActions,
            relatedTopics: aiResponse?.relatedTopics
          },
          created_at: new Date()?.toISOString()
        }
      ]);
    } catch (error) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: `I'm having trouble connecting right now. ${error?.message}`,
          created_at: new Date()?.toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleContentClick = async (content) => {
    await aiHelpService?.trackInteraction(
      user?.id,
      'content_viewed',
      getScreenName(location?.pathname),
      content?.id
    );
  };

  return (
    <>
      {/* Floating Help Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-primary text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center z-50 hover:scale-110"
        aria-label="AI Help Assistant"
      >
        {isOpen ? (
          <Icon name="X" size={24} />
        ) : (
          <Icon name="MessageCircleQuestion" size={24} />
        )}
      </button>

      {/* Help Panel */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 h-[600px] bg-card border border-border rounded-lg shadow-2xl z-50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-primary text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Icon name="Sparkles" size={20} />
              </div>
              <div>
                <h3 className="font-semibold">AI Help Assistant</h3>
                <p className="text-xs opacity-90">Here to help you navigate TrainaryGym</p>
              </div>
            </div>
          </div>

          {/* View Tabs */}
          <div className="border-b border-border bg-background">
            <div className="flex">
              {[
                { id: 'chat', label: 'Chat', icon: 'MessageSquare' },
                { id: 'library', label: 'Help Library', icon: 'BookOpen' },
                { id: 'topics', label: 'Quick Topics', icon: 'Zap' }
              ]?.map(view => (
                <button
                  key={view?.id}
                  onClick={() => setActiveView(view?.id)}
                  className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${
                    activeView === view?.id
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={view?.icon} size={16} />
                  {view?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-hidden">
            {activeView === 'chat' && (
              <ChatInterface
                messages={messages}
                onSendMessage={handleSendMessage}
                loading={loading}
                userRole={userProfile?.role}
              />
            )}

            {activeView === 'library' && (
              <HelpContentLibrary
                content={helpContent}
                onContentClick={handleContentClick}
                userRole={userProfile?.role}
              />
            )}

            {activeView === 'topics' && (
              <QuickHelpTopics
                userRole={userProfile?.role}
                currentScreen={getScreenName(location?.pathname)}
                onTopicClick={(topic) => {
                  setActiveView('chat');
                  handleSendMessage(topic);
                }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AIHelpAssistant;